<template>
  <Box v-if="isCalling" :variant="boxVariant">
    <Media align="middle">
      <MediaBody>
        <h2 class="h3 mrgt0">
          {{ title }}
        </h2>
        <div>
          {{ startedAt ? callDuration : $t('voip.call.initializing') }}
        </div>
      </MediaBody>
      <MediaRight>
        <Btn
          v-if="microphoneMuted"
          variant="outline"
          class="mute-unmute-button"
          :title="$t('voip.call.unmute')"
          size="small"
          @click.prevent="unmute"
        >
          <Icon id="mute" :scale="1" />
        </Btn>
        <Btn
          v-else
          variant="outline"
          class="mute-unmute-button"
          :title="$t('voip.call.mute')"
          size="small"
          @click.prevent="mute"
        >
          <Icon id="unmute" :scale="1" />
        </Btn>
        <Btn
          :variant="btnVariant"
          size="large"
          data-test="hangup-button"
          class="hangup-button"
          :title="$t('voip.call.hang-up')"
          @click.prevent="hangup"
        >
          <Icon id="phone-down" :scale="2" />
        </Btn>
      </MediaRight>
    </Media>

    <CallParticipants v-if="participants" :participants="participants" />
  </Box>
  <Box
    v-else-if="connection"
    size="small"
    class="incoming-call box--rounded box--floating"
  >
    <Box size="small" class="incoming-call__caller box--rounded">
      <UserEntity v-if="callerUser" :user="callerUser" size="large" />
      <Entity v-else :type="callerType" :title="callerName" size="large" />
    </Box>
    <ul
      class="list-inline list-inline--justified pdgt pdgb- pdgh-"
      role="presentation"
    >
      <li role="presentation">
        <Btn
          data-test="accept-button"
          variant="default"
          size="large"
          @click.prevent="acceptIncomingCall"
        >
          <Icon id="phone-up" :scale="1.25" class="mrgr--" />
          {{ $t('voip.call.answer') }}
        </Btn>
      </li>
      <li role="presentation">
        <Btn
          data-test="reject-button"
          variant="danger"
          @click.prevent="rejectIncomingCall"
        >
          <Icon id="phone-down" class="mrgr--" />
          {{ $t('voip.call.reject') }}
        </Btn>
      </li>
    </ul>
  </Box>
</template>

<script>
import dayjs from 'dayjs';
import { mapState, mapActions, mapMutations, mapGetters } from 'vuex';

import { getConference } from '@/services/voip';
import {
  twilioDevice,
  createDevice,
  destroyDevice,
  twilioConnection,
  setConnection,
} from '@/services/twilio';

import Entity from '@/components/ui/Entity';
import UserEntity from '@/components/users/UserEntity';
import CallParticipants from '@/components/voip/CallParticipants';

export default {
  components: {
    Entity,
    UserEntity,
    CallParticipants,
  },

  data() {
    return {
      registered: false,
      twilioConnection,
      startedAt: null,
      now: Date.now(),
      durationInterval: null,
      statusInterval: null,
    };
  },

  computed: {
    ...mapState('voip', [
      'token',
      'conference',
      'isCalling',
      'status',
      'callType',
      'microphoneMuted',
      'awaitingSelfConnect',
    ]),
    ...mapGetters('auth', ['currentUser']),

    title() {
      return this.callType !== 'emergency'
        ? this.$t('voip.call.in-progress')
        : this.$t('voip.call.emergency-in-progress');
    },

    device() {
      return twilioDevice;
    },

    connection: {
      get() {
        return this.twilioConnection.connection;
      },
      set(val) {
        setConnection(val);
      },
    },

    boxVariant() {
      return this.callType === 'emergency' ? 'danger' : 'primary';
    },

    btnVariant() {
      return this.callType === 'emergency' ? 'danger-inverted' : 'danger';
    },

    callerUser() {
      // No participant has is_initiator when the initiator is the current user themselves
      // (they're filtered out of `participants`), so `find` can legitimately find nothing.
      const initiator = this.participants.find(
        participant => participant.is_initiator
      );
      return initiator ? initiator.user : null;
    },

    callerType() {
      return (
        (this.conference && this.conference.call_info) || this.connectionType
      );
    },

    connectionType() {
      return (
        this.connection &&
        this.connection.parameters.From &&
        this.connection.parameters.From.split(':')[1]
      );
    },

    callerName() {
      return (
        (this.conference && this.conference.description) ||
        this.$t('voip.call.caller.unknown')
      );
    },

    callDuration() {
      if (this.startedAt) {
        return dayjs(dayjs(this.now).diff(this.startedAt)).format('mm:ss');
      }
      return '';
    },

    /**
     * Conference invitees includes the currently logged in user, we want only the other users
     */
    participants() {
      if (this.conference && this.conference.invitees) {
        return this.conference.invitees.filter(
          invitee => invitee.user.username !== this.currentUser.username
        );
      }
      return [];
    },
  },

  created() {
    this.getTwilioTokenAndInitDevice();

    // Safety net: `status` is otherwise only recomputed inside specific Twilio SDK event
    // handlers. If a connection ends without cleanly firing one of them (e.g. a WebRTC/media
    // failure), the call button can stay disabled indefinitely. Re-syncing periodically bounds
    // how long a stuck state can last.
    this.statusInterval = setInterval(this.updateStatus, 5000);
  },

  beforeDestroy() {
    this.clearCall();
    destroyDevice();
    clearInterval(this.statusInterval);
  },

  methods: {
    ...mapActions('voip', [
      'getTwilioToken',
      'disconnectDevice',
      'updateStatus',
      'clearCall',
      'increaseMissedCalls',
      'setMicrophoneMuted',
    ]),
    ...mapMutations('voip', [
      'setIsCalling',
      'setConference',
      'setCallType',
      'setAwaitingSelfConnect',
    ]),

    getTwilioTokenAndInitDevice() {
      this.getTwilioToken().then(this.initDevice, error => {
        this.$notify({
          type: 'error',
          title: this.$t('voip.label'),
          text: error,
          duration: -1,
        });
      });
    },

    initDevice() {
      if (twilioDevice) {
        // Failures here (e.g. no transport available) also emit a device 'error' event,
        // already handled by handleError() - this only prevents an unhandled rejection.
        // updateToken() doesn't always return a promise (observed in production), so wrap
        // it rather than assume it does.
        Promise.resolve(twilioDevice.updateToken(this.token)).catch(() => {});
      } else {
        createDevice(this.token);
        this.device.on('error', this.handleError);
        this.device.on('registered', this.handleRegistered);
        this.device.on('unregistered', this.handleOffline);
        this.device.on('incoming', this.handleIncoming);
      }
    },

    updateConference() {
      // mute the operator microphone if it is an emergency call
      try {
        if (this.microphoneMuted) {
          this.connection.mute(true);
        } else {
          this.connection.mute(false);
        }
      } catch (error) {}

      this.now = Date.now();
      // Save the request to avoid doing it again if the previous one was not back yet
      if (this.connection && !this.conferenceRequest) {
        this.conferenceRequest = getConference(
          this.connection.parameters.CallSid
        )
          .then(response => {
            // Sometimes a request starts during the call but arrives after the call ended;
            // In this case, we discard the response to keep the conference as `null`
            // Otherwise when the next call starts, it will initially display the previous conference
            if (this.isCalling) {
              this.handleConferenceResponse(response);
            }
          })
          .finally(() => {
            this.conferenceRequest = null;
          });
      }
    },

    handleConferenceResponse(response) {
      this.setConference(response.data[0]);
    },

    /**
     * When Twilio Device is ready to receive calls
     */
    handleRegistered() {
      this.registered = true;
      this.updateStatus();
    },

    /**
     * When an error occurs on Twilio side
     */
    handleError(error) {
      this.updateStatus();

      // Token has expired, let's refresh it
      if ([31204, 31205, 31005, 31009, 20104, 20101].includes(error.code)) {
        this.getTwilioTokenAndInitDevice();
      } else {
        this.$notify({
          type: 'error',
          title: `Twilio error ${error.code}`,
          text: error.message,
          duration: -1,
        });
      }
    },

    /**
     * When the call is accepted
     */
    handleConnect() {
      this.updateStatus();
      this.setIsCalling(true);
      this.startedAt = Date.now();
      this.updateConference();
      this.durationInterval = setInterval(this.updateConference, 1000);
    },

    /**
     * When the call is cancelled by the caller or not answered
     */
    handleCancel() {
      this.updateStatus();
      this.clearCall();
      this.increaseMissedCalls();
    },

    /**
     * When a call comes in (neither accepted or rejected)
     */
    handleIncoming(call) {
      this.updateStatus();
      this.connection = call;
      this.connection.on('accept', this.handleConnect);
      this.connection.on('disconnect', this.handleDisconnect);
      this.connection.on('cancel', this.handleCancel);

      // In case the call was initiated by us (tracked locally - the backend's caller identity
      // for this leg, 'client:central', is ambiguous: it also means "an operator-type account
      // called the operator queue", see SELF_CONNECT_TIMEOUT_MS in store/modules/voip.js)
      // we already got the conference object back from the "call" request, no need to fetch it.
      // We answer Twilio's call directly because it is our own initiative.
      if (this.awaitingSelfConnect) {
        this.setAwaitingSelfConnect(false);
        this.setIsCalling(true);
        this.acceptIncomingCall();
      }
      // Otherwise we fetch the conference data to display the caller infos
      else {
        getConference(this.connection.parameters.CallSid).then(
          this.handleConferenceResponse
        );
      }
    },

    /**
     * When the call ends
     */
    handleDisconnect() {
      this.updateStatus();
      this.clearCall();
    },

    /**
     * When the connection with Twilio drops or the token has expired
     */
    handleOffline() {
      this.updateStatus();

      // Ensure the device has been ready once before trying to init it again
      // Otherwise we can get an infinite loop of device initialization
      // in case something's wrong with Twilio’s config
      if (this.registered) {
        this.registered = false;
      }
    },

    /**
     * Clean-up all data related to the call to start fresh next time
     */
    clearCall() {
      setConnection(null);
      this.setConference(null);

      clearInterval(this.durationInterval);
      this.startedAt = null;
      this.now = null;
      this.setIsCalling(false);
      this.setCallType(null);
    },

    acceptIncomingCall() {
      this.connection.accept();
    },

    rejectIncomingCall() {
      this.connection.reject();
      this.clearCall();
    },

    hangup() {
      this.disconnectDevice();
    },

    mute() {
      this.setMicrophoneMuted(true);
    },

    unmute() {
      this.setMicrophoneMuted(false);
    },
  },
};
</script>

<style lang="scss" scoped>
.incoming-call {
  width: 400px;
  max-width: calc(100% - #{$spacing-unit-default * 2});

  position: absolute;
  top: $spacing-unit-default;
  right: $spacing-unit-default;
  z-index: 100;

  background-color: $warning-400;
}

.incoming-call__caller {
  background-color: rgba(255, 255, 255, 0.4);
}

.hangup-button {
  width: 4.2em;
  height: 4.2em;
}

.mute-unmute-button {
  width: 4.2em;
  height: 4.2em;
  border: none;
  margin-right: 20px;
}
</style>
